/**
 * The two albedo ramps, resolved from Oklch stops into linear-light textures.
 *
 * Interpolating in Oklch rather than between hex bytes is what keeps the
 * lavender-to-magenta ramp from dipping through a muddy mid-tone, and the
 * texture is half-float linear so nothing downstream has to guess whether a
 * sampled value is albedo or screen colour: it is always albedo.
 */

import * as THREE from "three";

import { POPULATION_PALETTE_STOPS, TERRAIN_PALETTE_STOPS } from "../../shared/constants.ts";
import { oklchToGamut, sampleOklchStops, type Oklch } from "../../shared/oklab.ts";

const PALETTE_SIZE = 512;

export interface ReliefPalettes {
  terrain: THREE.DataTexture;
  population: THREE.DataTexture;
}

export function buildPaletteTextures(): ReliefPalettes {
  return {
    terrain: buildPaletteTexture(TERRAIN_PALETTE_STOPS),
    population: buildPaletteTexture(POPULATION_PALETTE_STOPS),
  };
}

function buildPaletteTexture(
  stops: ReadonlyArray<readonly [number, Oklch]>,
): THREE.DataTexture {
  const data = new Uint16Array(PALETTE_SIZE * 4);
  const one = THREE.DataUtils.toHalfFloat(1);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const linear = oklchToGamut(
      sampleOklchStops(stops, index / (PALETTE_SIZE - 1)),
    );
    const target = index * 4;
    data[target] = THREE.DataUtils.toHalfFloat(linear[0]);
    data[target + 1] = THREE.DataUtils.toHalfFloat(linear[1]);
    data[target + 2] = THREE.DataUtils.toHalfFloat(linear[2]);
    data[target + 3] = one;
  }

  const texture = new THREE.DataTexture(
    data,
    PALETTE_SIZE,
    1,
    THREE.RGBAFormat,
    THREE.HalfFloatType,
  );
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // Already linear reflectance: any colour-space conversion here would be a
  // second, wrong decode.
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}
